/* eslint-disable camelcase */
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import dateFormat from 'dateformat';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { UserProfession, UserSubProfession } from '../../../enums/UserProfession';
import '../../../themes/JobDetailsBox.scss';
import { useShouldEnableNCNSRepostStates } from '../../../hooks/useShouldEnableNCNSRepostStates';
import {
  getOffersToBestProfessionalsValue,
  getShouldShowWhoCanSeeThisShift,
  setJobAttributes,
} from '../../../growthbook';
import { NCNSRepostForm } from './NCNSRepost/NCNSRepostForm';
import Tooltip from '../../commonComponents/Tooltip';
import { createLoadingSelector } from '../../../apis/selectors';

const ALERT_ACTIONS = ['FETCH_JOB_NOTIFICATION', 'RETRIGGER_JOB_NOTIFICATION'];

export const JobDetailsBox = ({
  job_detail: jobDetail,
  posting,
  onEdit,
  onCancel,
  onSendAlert,
  openMessages,
  theOneWhoPosted,
}) => {
  const {
    professionName,
    subProfession,
    transaction_id: transactionId,
    messages,
    confirmAttendance,
    confirmPerformedBy,
    author,
    urgentShift,
    repost,
    workerClassification,
    autoFill,
    autofillOptions,
    isAutoFilled,
    lunch_break_time: lunchBreakTime,
    is_lunch_break_paid: isLunchBreakPaid,
    rate,
    original_rate: originalRate,
    regularAndOvertimeHours,
    local_start_time: localStartTime,
    local_end_time: localEndTime,
    local_date: localDate,
    job_filled_datetime: jobFilledDatetime,
    job_datetime: jobDateTimeStart,
    job_datetime_end: jobDatetimeEnd,
    createdAt,
    id: jobDetailId,
    procedures,
    specialty,
    isAssistedHygiene,
    profession,
    status,
    incident,
    repostMaxStartTime,
    state,
    jobDeepLink,
    isOnsiteInterview,
  } = jobDetail || {};

  const loadingSelector = createLoadingSelector(ALERT_ACTIONS);
  const isAlertLoading = useSelector((state) => loadingSelector(state));
  const notification = useSelector((state) => state.jobs.notification);
  const allMetadata = useSelector((state) => state.users.allMetadata);
  const jobNotifConfig = allMetadata?.jobNotification || {};
  const { checkShouldEnableNCNSRepost } = useShouldEnableNCNSRepostStates();
  const jobProfessionName = UserSubProfession.getName(subProfession) ?? professionName;
  const whoCanSeeThisShiftStatus = (() => {
    const offersToBestProfessional = getOffersToBestProfessionalsValue();

    if (
      !createdAt ||
      !getShouldShowWhoCanSeeThisShift() ||
      !localDate ||
      !localStartTime ||
      !offersToBestProfessional
    ) {
      return null;
    }

    const { minimum_hours_to_job_date, hours_to_hide, professions, states } =
      offersToBestProfessional;

    if (!professions.includes(profession) || !states.includes(state)) {
      return null;
    }

    const nowAt = moment();
    const shiftCreatedAt = moment(createdAt);
    const shiftStartsAt = moment(`${localDate} ${localStartTime}`, 'ddd, MMM DD, YYYY hh:mm a');

    const totalTime = shiftStartsAt.diff(shiftCreatedAt, 'hours');
    const isShowAllWhenReachDefinedHours = nowAt.diff(shiftCreatedAt, 'hours') > hours_to_hide;

    if (totalTime <= minimum_hours_to_job_date) {
      return 'All Professionals';
    }
    const status = jobDetail?.is_cancelled ? 'cancelled' : jobDetail.status;
    if (status === 'open' && isShowAllWhenReachDefinedHours) {
      return 'All Professionals';
    }

    return 'Level 1-3 Professionals';
  })();

  useEffect(() => {
    setJobAttributes(jobDetail);
  }, [jobDetail]);

  const renderCancelButton = () => {
    if (jobDetail.is_cancelled === false) {
      return (
        <div>
          <button
            className="btn btn-danger mb-2 mr-2"
            type="button"
            style={{
              width: 150,
              borderRadius: 10,
              marginTop: '12px',
            }}
            onClick={onCancel}
          >
            Cancel {isOnsiteInterview ? 'Interview' : 'Shift'}
          </button>
        </div>
      );
    }

    return <div />;
  };

  const renderEditButton = () => (
    <button
      onClick={onEdit}
      className="btn btn-info mb-2"
      type="button"
      style={{ width: 150, marginTop: '12px', borderRadius: 10 }}
    >
      Edit
    </button>
  );

  const renderSendAlertButton = () => {
    if (isOnsiteInterview) return null;

    const isValidStatus = ['open', 'pending', 'counter', 'pending-counter'].includes(status);

    const isBeforeRetriggerWindow = moment().isBefore(
      moment(jobDateTimeStart).subtract(jobNotifConfig?.retriggerThresholdDays, 'days'),
    );

    const isInRetriggerCooldown =
      notification?.retriggerAt &&
      moment(notification.retriggerAt)
        .add(jobNotifConfig?.minHoursBeforeRetrigger, 'hours')
        .isAfter(moment());

    const hasExceededMaxRetriggers =
      (notification?.retriggerCount ?? 0) >= jobNotifConfig?.maxRetriggerCount;

    const isButtonDisabled =
      isAlertLoading ||
      !isValidStatus ||
      isBeforeRetriggerWindow ||
      isInRetriggerCooldown ||
      hasExceededMaxRetriggers;

    return (
      <div
        className="mb-2"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <button
          onClick={onSendAlert}
          disabled={isButtonDisabled}
          className="btn btn-success mb-2 mr-2"
          type="button"
          style={{ width: 150, marginTop: 12, borderRadius: 10 }}
        >
          Send Alert
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          ({notification?.retriggerCount} of {jobNotifConfig?.maxRetriggerCount} alerts used)
          <Tooltip
            content={
              <>
                <strong>Rules:</strong>
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 10 }}>
                  <span>• Only {jobNotifConfig?.maxRetriggerCount} alerts permitted per shift</span>
                  <span>
                    • Can only send {jobNotifConfig?.retriggerThresholdDays} days before shift
                    starts
                  </span>
                  <span>
                    • Can only send more than {jobNotifConfig?.minHoursBeforeRetrigger} hours after
                    last alert was sent
                  </span>
                </div>
              </>
            }
          />
        </div>
      </div>
    );
  };

  const renderNCNSRepostButton = () => {
    const isPayoutOrCancelled = status === 'pay-out' || status === 'cancelled';
    const isNoIncidentReportedOrHasReposted = !incident || repostMaxStartTime;

    /**
     * show ncns repost button:
     * 1. when job is payout/within time of job or job is cancelled
     * 2. when job has no reported incident or already reposted
     * 3. when ff is on
     */
    const isShowNcnsRepostButton =
      isPayoutOrCancelled && isNoIncidentReportedOrHasReposted && checkShouldEnableNCNSRepost();

    if (isShowNcnsRepostButton) {
      return (
        <NCNSRepostForm
          jobId={jobDetailId}
          jobDateTimeStart={jobDateTimeStart}
          jobDatetimeEnd={jobDatetimeEnd}
          repostMaxStartTime={repostMaxStartTime}
        />
      );
    }
    return <div />;
  };
  const renderDynamicLink = () => {
    const jobLink = jobDeepLink?.jobLink
      ? jobDeepLink.jobLink
      : `${process.env.REACT_APP_JOB_DNT_LINK}${jobDetailId}`;
    const shiftLink = jobDeepLink?.shiftLink
      ? jobDeepLink.shiftLink
      : `${process.env.REACT_APP_JOB_HYG_LINK}${jobDetailId}`;

    return (
      <div className="dynamic-links">
        <p>
          <b>Link to job for the office :</b>
        </p>
        <p>
          {jobLink}
          <button
            type="button"
            className="btn btn-info mr-1 ml-3"
            onClick={() => navigator.clipboard.writeText(jobLink)}
          >
            Copy
          </button>
        </p>
        <p>
          <b>Link to open shift for Professional :</b>
        </p>
        <p>
          {shiftLink}
          <button
            type="button"
            className="btn btn-info mr-1 ml-3"
            onClick={() => navigator.clipboard.writeText(shiftLink)}
          >
            Copy
          </button>
        </p>
      </div>
    );
  };

  const preferType =
    {
      more_experience: 'Most Experience',
      firs_available: '1st Available',
      closer_to_me: 'Closer To Me',
    }[jobDetail.prefer_type] ?? '-';

  return (
    <div className="card mt-4 mb-4 job-details-box">
      <div className="card-body container">
        <h3 className="card-title mt-2 title">
          {isOnsiteInterview ? 'Interview' : 'Shift'} Details
        </h3>
        <div className="row">
          <p className="card-text row-title">Job ID</p>
          <p className="card-text">{transactionId || ''}</p>
        </div>
        <div className="row">
          <p className="card-text row-title">Created</p>
          <p className="card-text">
            {dateFormat(createdAt, 'dddd, mmmm dS, yyyy, h:MM:ss TT') || ''}
          </p>
        </div>
        <div className="row">
          <p className="card-text row-title">Filled</p>
          <p className="card-text">
            {jobFilledDatetime
              ? dateFormat(jobFilledDatetime, 'dddd, mmmm dS, yyyy, h:MM:ss TT')
              : '-'}
          </p>
        </div>
        {isOnsiteInterview && (
          <div className="row">
            <p className="card-text row-title">Job Listing Title</p>
            <p className="card-text">
              <Link
                className="card-text"
                to={{
                  pathname: `/permanent-job/office/${posting?.officeId}/job/${posting?.id}`,
                }}
              >
                {posting?.title}
              </Link>
            </p>
          </div>
        )}
        <div className="row">
          <p className="card-text row-title">Profession</p>
          <p className="card-text">{jobProfessionName || '-'}</p>
        </div>
        <div className="row">
          <p className="card-text row-title">Skills</p>
          <p className="card-text" style={{ textTransform: 'capitalize' }}>
            {specialty?.split('_')?.[1] || '-'}
          </p>
        </div>
        <div className="row">
          <p className="card-text row-title">
            {isOnsiteInterview ? 'Interview' : 'Shift'} Procedures
          </p>
          <p
            className="card-text"
            style={{ textTransform: 'capitalize', maxWidth: '50%', textAlign: 'right' }}
          >
            {(procedures ?? [])
              .map((procedure) => procedure.replace(/^procedure_/g, ''))
              .join(', ')}
          </p>
        </div>
        <div className="row">
          <p className="card-text row-title">Date</p>
          <p className="card-text">{localDate || ''}</p>
        </div>
        <div className="row">
          <p className="card-text row-title">Time</p>
          <p className="card-text">{`${localStartTime} - ${localEndTime}`}</p>
        </div>
        <div className="row">
          <p className="card-text row-title">Regular hours</p>
          <p className="card-text">
            {regularAndOvertimeHours?.regular
              ? `${regularAndOvertimeHours?.regular} hr${
                  regularAndOvertimeHours?.regular > 1 ? 's' : ''
                }`
              : '-'}
          </p>
        </div>
        <div className="row">
          <p className="card-text row-title">Overtime hours (1.5 * rate)</p>
          <p className="card-text">
            {regularAndOvertimeHours?.overtime
              ? `${regularAndOvertimeHours?.overtime} hr${
                  regularAndOvertimeHours?.overtime > 1 ? 's' : ''
                }`
              : '-'}
          </p>
        </div>

        {jobDetail.original_rate && (
          <div className="row">
            <p className="card-text row-title">Original Rate</p>
            <p className="card-text">{originalRate.toFixed(2) || ''}</p>
          </div>
        )}

        <div className="row">
          <p className="card-text row-title">Rate</p>
          <p className="card-text">{Number.isInteger(rate) ? rate?.toFixed(2) : rate}</p>
        </div>

        <div className="row">
          <p className="card-text row-title">Meal Break Paid</p>
          <p className="card-text">{isLunchBreakPaid ? 'Yes' : 'No' || ''}</p>
        </div>
        <div className="row">
          <p className="card-text row-title">Meal Break Time</p>
          <p className="card-text">{`${lunchBreakTime}` || ''}</p>
        </div>
        {!!whoCanSeeThisShiftStatus && (
          <div className="row">
            <p className="card-text row-title">Who can see this shift</p>
            <p className="card-text">{whoCanSeeThisShiftStatus}</p>
          </div>
        )}
        {profession === UserProfession.RDH && (
          <div className="row">
            <p className="card-text row-title">Assisted Hygiene Available</p>
            <p className="card-text">{isAssistedHygiene ? 'Yes' : 'No'}</p>
          </div>
        )}
        <div className="row">
          <p className="card-text row-title">Auto-Confirm (Favorites)</p>
          {isOnsiteInterview ? (
            <p className="card-text">-</p>
          ) : (
            <p className="card-text">{autofillOptions?.favorite ? 'On' : 'Off' || ''} </p>
          )}
        </div>
        <div className="row">
          <p className="card-text row-title">Auto-Confirm (Highly-Rated)</p>
          {isOnsiteInterview ? (
            <p className="card-text">-</p>
          ) : (
            <p className="card-text">{autofillOptions?.highlyRated ? 'On' : 'Off' || ''} </p>
          )}
        </div>
        <div className="row">
          <p className="card-text row-title">Auto-Confirm (All Eligible)</p>
          {isOnsiteInterview ? (
            <p className="card-text">-</p>
          ) : (
            <p className="card-text">
              {autoFill ? 'On' : 'Off' || ''} {isAutoFilled ? '(Auto-filled)' : ''}
            </p>
          )}
        </div>

        <div className="row">
          <p className="card-text row-title">Worker Type</p>
          <p className="card-text">{workerClassification || '-'}</p>
        </div>
        <div className="row">
          <p className="card-text row-title">Dentist Prefer</p>
          <p className="card-text">{`${preferType}` || ''}</p>
        </div>

        <div className="row">
          <p className="card-text row-title">Reposted</p>
          <p className="card-text">{repost ? 'Yes' : 'No'}</p>
        </div>

        {!isOnsiteInterview && (
          <div className="row">
            <p className="card-text row-title">Urgent Shift</p>
            <p className="card-text">{urgentShift ? 'Yes' : 'No'}</p>
          </div>
        )}

        <div className="row">
          <p className="card-text row-title">Extra Pay</p>
          <p className="card-text">{urgentShift?.bonusPay?.toFixed(2) ?? '-'}</p>
        </div>

        <div className="row">
          <p className="card-text row-title">Posted By</p>
          <p className="card-text">{theOneWhoPosted(author)}</p>
        </div>
        <div className="row">
          <p className="card-text row-title">Confirmed By</p>
          <p className="card-text">{theOneWhoPosted(confirmPerformedBy)}</p>
        </div>
        <div className="row">
          <p className="card-text row-title">Professional Confirmation</p>
          <p className="card-text">
            {confirmAttendance?.confirmationDate
              ? dateFormat(confirmAttendance?.confirmationDate, 'dddd, mmmm dS, yyyy, h:MM:ss TT')
              : '-'}
          </p>
        </div>
      </div>

      {renderDynamicLink()}

      {messages?.length !== 0 && (
        <div>
          <button
            className="btn btn-success mb-4 mr-2"
            type="button"
            style={{
              width: 150,
              borderRadius: 10,
              marginTop: '12px',
            }}
            onClick={() => openMessages()}
          >
            Open Job Messages
          </button>
        </div>
      )}

      {renderEditButton()}
      {renderCancelButton()}
      {renderSendAlertButton()}
      {renderNCNSRepostButton()}

      <span className="mb-2" />
    </div>
  );
};

export default JobDetailsBox;
